import {Component, computed, inject, input, DestroyRef, ChangeDetectionStrategy} from "@angular/core"
import {toObservable, toSignal} from "@angular/core/rxjs-interop"
import {MatTooltipModule} from "@angular/material/tooltip"
import {RouterModule} from "@angular/router"
import {Settings} from "@app/common/models/settings/settings"
import {TemplateReference, TemplateLikeNode} from "@cm/template-nodes"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {GetTemplateDetailsForTemplateThumbnailGQL} from "@template-editor/components/template-thumbnail/template-thumbnail.generated"
import {from, switchMap, of, catchError} from "rxjs"
import {CardComponent} from "@app/common/components/cards/card/card.component"
import {DataObjectThumbnailComponent} from "@app/common/components/data-object-thumbnail/data-object-thumbnail.component"
import {ThumbnailComponent} from "@app/common/components/thumbnail/thumbnail.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-thumbnail",
    templateUrl: "./template-thumbnail.component.html",
    styleUrl: "./template-thumbnail.component.scss",
    imports: [CardComponent, DataObjectThumbnailComponent, ThumbnailComponent, RouterModule, MatTooltipModule],
})
export class TemplateThumbnailComponent {
    private readonly templateGql = inject(GetTemplateDetailsForTemplateThumbnailGQL)
    protected readonly destroyRef = inject(DestroyRef)

    readonly $templateLike = input.required<TemplateLikeNode>({alias: "template"})
    private readonly $templateReference = computed(() => {
        const template = this.$templateLike()
        if (!(template instanceof TemplateReference)) return undefined
        return template
    })

    readonly $templateId = computed(() => {
        const templateReference = this.$templateReference()
        if (!templateReference) return undefined
        return templateReference.parameters.templateId
    })

    private readonly $templateData = toSignal(
        toObservable(this.$templateId).pipe(
            switchMap((templateId) => {
                if (templateId === undefined) return of(null)
                return from(fetchThrowingErrors(this.templateGql)({legacyId: templateId}))
            }),
            catchError(() => of(null)),
        ),
        {
            initialValue: null,
        },
    )
    readonly $thumbnailUrl = computed(() => {
        const templateRevisionData = this.$templateData()
        if (!templateRevisionData) return Settings.IMAGE_NOT_AVAILABLE_SMALL_URL
        return undefined
    })
    readonly $thumbnailObject = computed(() => {
        const templateRevisionData = this.$templateData()
        if (templateRevisionData) return templateRevisionData.template.galleryImage
        return undefined
    })
    readonly $title = computed(() => {
        const templateRevisionData = this.$templateData()
        if (!templateRevisionData) return ""

        const {template} = templateRevisionData

        return `${template.legacyId} - ${template.name}`
    })
}
